import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { appSelector } from "../../Components/redux/selector";
import { userSelector } from "../../Components/user-redux/selector";
import Key from "../../clientVariables.json";
import parse from "html-react-parser";
import {
  Paper,
  Grid,
  Typography,
  Box,
  Avatar,
  ListItemButton,
  useMediaQuery,
  useTheme,
  Stack,
  Button,
  Skeleton,
  Chip,
} from "@mui/material";
import moment from "moment";
import { ArrowBack } from "@mui/icons-material";
import {
  BusinessCenterRounded,
  ChecklistRtlRounded,
} from "@mui/icons-material";

const InterviewsTab = () => {
  const { user } = useSelector(userSelector);
  const { primaryResumeDetails, candidateSelectedResume } =
    useSelector(appSelector);
  const [selectedItem, setSelectedItem] = useState(0);
  const [submittedCandidates, setSubmittedCandidates] = useState([]);
  const [page] = useState(0);
  const [limit] = useState(200);
  const [expanded, setExpanded] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const theme = useTheme();

  const isDesktopXS = useMediaQuery(
    "(min-width: 992px) and (max-width: 1199px)"
  );
  const isDesktopMedium = useMediaQuery(
    "(min-width: 1440px) and (max-width: 1919px)"
  );
  const isMobileSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const isMobile = useMediaQuery(theme.breakpoints.between("xs", "sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isDesktopExtraSmall = useMediaQuery(
    "(min-width: 992px) and (max-width: 1199px)"
  );

  const [jobSelectForMobileView, setJobSelectForMobileView] = useState(false);

  const interviewStageIds = [8, 11, 12];
  const filteredSubmissions =
    submittedCandidates?.filter((job) =>
      interviewStageIds.includes(job.bench_status_id)
    ) || [];

  const selectedJob = filteredSubmissions[selectedItem] || null;

  const detailsLoading = listLoading;

  const resumeSkillsSource =
    primaryResumeDetails?.ai_skills ||
    candidateSelectedResume?.ai_skills ||
    "";

  const jobSkillsList = (selectedJob?.required_skills || "")
    .split(",")
    .map((skill) => skill.trim())
    .filter(Boolean);

  const resumeSkillsNormalized = String(resumeSkillsSource)
    .split(",")
    .map((skill) => skill.trim().toLowerCase())
    .filter(Boolean);

  const isJobSkillMatched = (skill) =>
    resumeSkillsNormalized.includes(skill.trim().toLowerCase());

  const matchedJobSkillsCount = jobSkillsList.filter(isJobSkillMatched).length;

  const sortedJobSkills = [
    ...jobSkillsList.filter(isJobSkillMatched),
    ...jobSkillsList.filter((skill) => !isJobSkillMatched(skill)),
  ];

  const visibleJobSkills = expanded
    ? sortedJobSkills
    : sortedJobSkills.slice(0, 12);

  const jobMobileViewHandler = (isJobSelect) => {
    setJobSelectForMobileView(isJobSelect);
  };

  const handleListItemClick = (index) => {
    setSelectedItem(index);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    setExpanded(false);
  }, [selectedJob?.id, selectedItem]);

  const fetchData = async () => {
    try {
      setListLoading(true);
      const candidateid = user?.candidate_id;
      const fetchPage = page;
      const fetchLimit = limit;
      if (!candidateid) {
        setListLoading(false);
        return;
      }
      const response = await fetch(
        `${Key.domain}/candidate/${candidateid}/submissions/${fetchPage}/${fetchLimit}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await response.json();
      setSubmittedCandidates(data.data.submissions || []);
      setSelectedItem(0);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      setSubmittedCandidates([]);
    } finally {
      setListLoading(false);
    }
  };

  const getTimeAgo = (timestamp) => {
    const lastModifiedMoment = moment(timestamp);
    const now = moment();
    const diffInSeconds = now.diff(lastModifiedMoment, "seconds");

    if (diffInSeconds < 60) {
      return "a few seconds ago";
    } else {
      return lastModifiedMoment.fromNow();
    }
  };

  const handleToggleExpand = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  return (
    <Box
      sx={{
        borderRadius: "5px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Grid
        container
        sx={{ flexGrow: 1, height: "100%", margin: 0, width: "100%" }}
      >
        <Grid
          item
          xs={
            isMobile || isMobileSmall || isTablet
              ? 12
              : isDesktopSmall
                ? 4
                : isDesktopLarge
                  ? 3
                  : 3
          }
          sx={{
            display: isDesktop || jobSelectForMobileView ? "block" : "none",
            height: "100%",
            pt: "0 !important",
            pr: { xs: 0, md: 1 },
            pb: { xs: 1, md: 0 },
          }}
        >
          <Paper
            elevation={2}
            sx={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}
          >
            {listLoading ? (
              <Box sx={{ p: 1.5 }}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: 1.25,
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="75%" height={18} />
                      <Skeleton variant="text" width="55%" height={14} />
                      <Skeleton variant="text" width="40%" height={14} />
                    </Box>
                    <Skeleton variant="text" width={60} height={14} />
                  </Box>
                ))}
              </Box>
            ) : (
              filteredSubmissions.map((job, index) => (
                <ListItemButton
                  key={index}
                  selected={index === selectedItem}
                  onClick={() => {
                    handleListItemClick(index);
                    jobMobileViewHandler(false);
                  }}
                  sx={{
                    ".hide-icons": {
                      display: "none",
                    },
                    "&:hover": {
                      ".hide-icons": {
                        display: "block",
                      },
                    },
                    paddingLeft: "12px",
                  }}
                >
                  <div style={{ display: "flex", gap: 10, width: "100%" }}>
                    <Avatar
                      sx={{
                        height: isDesktopLarge
                          ? "40px"
                          : isDesktopMedium
                            ? "35px"
                            : isDesktopSmall
                              ? "32px"
                              : "28px",
                        width: isDesktopLarge
                          ? "40px"
                          : isDesktopMedium
                            ? "35px"
                            : isDesktopSmall
                              ? "32px"
                              : "28px",
                      }}
                    >
                      <BusinessCenterRounded
                        sx={{
                          color: "#fff",
                          height: isDesktopLarge
                            ? "24px"
                            : isDesktopMedium
                              ? "20px"
                              : isDesktopSmall
                                ? "16px"
                                : "14px",
                          width: isDesktopLarge
                            ? "24px"
                            : isDesktopMedium
                              ? "22px"
                              : isDesktopSmall
                                ? "18px"
                                : "16px",
                        }}
                      />
                    </Avatar>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <Stack direction="column">
                        <Typography
                          sx={{
                            fontSize: isDesktopXS ? "10px" : "12px",
                            fontWeight: 600,
                            margin: 0,
                            width: isDesktopLarge
                              ? "200px"
                              : isDesktopMedium
                                ? "150px"
                                : isDesktopSmall
                                  ? "100px"
                                  : "80px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {job.jobtitle}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: isDesktopXS ? "9px" : "11px",
                            margin: 0,
                          }}
                        >
                          {job.company_name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: isDesktopXS ? "9px" : "11px",
                            margin: 0,
                          }}
                        >
                          {job.location}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isDesktopXS ? "9px" : "11px",
                            padding: "2px",
                            backgroundColor: "rgba(255, 236, 179, 0.5)",
                          }}
                        >
                          {job.pStatusName}
                        </Typography>
                      </Stack>
                      <Box sx={{ width: "200px" }}>
                        <Typography
                          paragraph
                          sx={{
                            margin: 0,
                            color: "#757575",
                            fontWeight: 500,
                            fontSize: isDesktopLarge ? "0.65rem" : "0.60rem",
                          }}
                        >
                          {job && getTimeAgo(job.posted_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </div>
                </ListItemButton>
              ))
            )}
          </Paper>
        </Grid>
        <Grid
          item
          xs={
            isDesktopSmall || isDesktopExtraSmall
              ? 8
              : isDesktopMedium
                ? 9
                : isMobile || isMobileSmall || isTablet
                  ? 12
                  : 9
          }
          sx={{
            display: !jobSelectForMobileView ? "block" : "none",
            height: "100%",
            pt: "0 !important",
          }}
        >
          <Box
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box>
              <Button
                onClick={() => jobMobileViewHandler(true)}
                sx={{
                  display: { xs: "flex", sm: "flex", md: "none" },
                  justifyContent: "flex-end",
                  marginBottom: "10px",
                  width: "100%",
                }}
              >
                <ArrowBack /> &nbsp;Back
              </Button>
            </Box>
            <Paper
              elevation={2}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                p: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {detailsLoading ? (
                <Box sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Skeleton variant="text" width="70%" height={32} />
                    <Skeleton variant="text" width="45%" height={20} />
                    <Skeleton variant="text" width="35%" height={18} />
                    <Skeleton variant="rounded" height={28} width={120} />
                    <Skeleton variant="text" width="90%" height={18} />
                    <Skeleton variant="text" width="80%" height={18} />
                    <Skeleton variant="rounded" height={220} sx={{ mt: 1 }} />
                  </Stack>
                </Box>
              ) : selectedJob ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      bgcolor: "#fff",
                      px: 1.5,
                      pt: 1.5,
                      pb: 1.5,
                      borderBottom: "1px solid #e8eef2",
                      zIndex: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "24px",
                        fontWeight: 500,
                      }}
                    >
                      {selectedJob?.jobtitle || "No job title"}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(0, 0, 0, 0.6)",
                        fontSize: "13px",
                        marginTop: "8px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedJob?.company_name || "N/A"}
                      {" • "}
                      {selectedJob?.location
                        ? selectedJob.location
                        : parseInt(selectedJob?.remote) === 1
                          ? "Remote"
                          : "Remote"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    <Box sx={{ px: 1.5, pt: 1.5, pb: 1.5 }}>
                      <Stack direction="column" gap={1} sx={{ width: "100%" }}>
                        <Typography
                          sx={{
                            margin: 0,
                            display: "flex",
                            alignItems: "end",
                            gap: "2px",
                            fontSize: "12px",
                            minWidth: "80px",
                            maxWidth: isDesktopLarge ? "500px" : "200px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <BusinessCenterRounded
                            sx={{
                              height: isDesktopLarge ? 20 : 18,
                              width: isDesktopLarge ? 20 : 18,
                              color: "#757575",
                              marginRight: "5px",
                            }}
                          />
                          {selectedJob?.jobtype_name || "Full Time"}
                        </Typography>

                        <Box sx={{ width: "100%", mb: 1.5 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "rgba(0,0,0,0.7)",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                m: 0,
                                lineHeight: 1.2,
                              }}
                            >
                              <ChecklistRtlRounded
                                sx={{
                                  height: isDesktopLarge ? 20 : 18,
                                  width: isDesktopLarge ? 20 : 18,
                                  color: "#757575",
                                }}
                              />
                              Required Skills
                            </Typography>
                            {jobSkillsList.length > 0 && (
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: "rgba(0,0,0,0.55)",
                                  m: 0,
                                  lineHeight: 1.2,
                                }}
                              >
                                {matchedJobSkillsCount}/{jobSkillsList.length}{" "}
                                matched
                              </Typography>
                            )}
                          </Box>

                          {jobSkillsList.length > 0 ? (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                  width: "100%",
                                  pl: isDesktopLarge ? "25px" : "23px",
                                }}
                              >
                                {visibleJobSkills.map((skill, index) => {
                                  const matched = isJobSkillMatched(skill);
                                  return (
                                    <Chip
                                      key={`${skill}-${index}`}
                                      size="small"
                                      label={skill}
                                      sx={{
                                        height: 26,
                                        fontSize: 11,
                                        fontWeight: 500,
                                        bgcolor: matched
                                          ? "rgba(46, 125, 50, 0.08)"
                                          : "rgba(0, 0, 0, 0.04)",
                                        color: matched ? "#2e7d32" : "#616161",
                                        border: "1px solid",
                                        borderColor: matched
                                          ? "#2e7d32"
                                          : "#bdbdbd",
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                              {sortedJobSkills.length > 12 && (
                                <Button
                                  size="small"
                                  onClick={handleToggleExpand}
                                  sx={{
                                    mt: 0.75,
                                    mb: 0.5,
                                    ml: isDesktopLarge ? "25px" : "23px",
                                    textTransform: "none",
                                    fontSize: 12,
                                    color: "#406882",
                                    p: 0,
                                    minWidth: 0,
                                  }}
                                >
                                  {expanded
                                    ? "Show less"
                                    : `Show ${sortedJobSkills.length - 12} more`}
                                </Button>
                              )}
                            </>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "text.secondary",
                                pl: isDesktopLarge ? "25px" : "23px",
                              }}
                            >
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      <Box sx={{ mt: 1 }}>
                        <Typography
                          sx={{
                            m: 0,
                            mb: 0.5,
                            fontSize: 14,
                            fontWeight: "bold",
                            lineHeight: 1.3,
                          }}
                        >
                          Job Details:
                        </Typography>
                        <Box
                          sx={{
                            fontSize: "12px",
                            m: 0,
                            p: 0,
                            lineHeight: 1.4,
                            "& *": { marginTop: "0 !important" },
                            "& p, & div, & span, & h1, & h2, & h3, & h4, & h5, & h6":
                              {
                                marginBottom: "6px",
                              },
                            "& p:empty, & div:empty": { display: "none" },
                            "& ul, & ol": {
                              margin: "4px 0",
                              paddingLeft: 2.5,
                            },
                          }}
                        >
                          {(() => {
                            const raw = selectedJob?.job_description;
                            if (!raw) return "No job description available";

                            const cleaned = String(raw)
                              .replace(
                                /^(\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<p>&nbsp;<\/p>|<div>\s*<\/div>)+/gi,
                                ""
                              )
                              .trim();

                            if (!cleaned) return "No job description available";

                            const looksLikeBrokenClassHtml =
                              cleaned.includes("ahref") ||
                              cleaned.includes("divclass") ||
                              cleaned.includes("aclass") ||
                              cleaned.includes("pclass") ||
                              cleaned.includes("liclass");

                            return looksLikeBrokenClassHtml
                              ? cleaned.replace(/<[^>]+>/g, "")
                              : parse(cleaned);
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 1.5 }}>
                  <Typography>No data available</Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InterviewsTab;
